// ─────────────────────────────────────────────────────────────────────────────
// useOarlockBleConnection — Thin React hook around the BLE driver singletons.
//
// Exposes a per-vendor singleton driver so multiple UI surfaces (Settings card,
// Monitor connect-toast, Stroke Visualizer) all reflect the same connection
// state. Returns { driver, status, connect, disconnect, discover }.
//
// Driver methods that talk to BLE are async and the hook funnels errors back
// into toast-friendly strings.
// ─────────────────────────────────────────────────────────────────────────────

import { useCallback, useEffect, useState } from 'react';
import NKEmpowerDriver from '../services/oarlock/drivers/NKEmpowerDriver';
import XBoatDriver from '../services/oarlock/drivers/XBoatDriver';

const SINGLETONS = {
  nk_empower: new NKEmpowerDriver(),
  xboat: new XBoatDriver(),
};

export function getOarlockDriver(vendor) {
  return SINGLETONS[vendor] || null;
}

export default function useOarlockBleConnection(vendor) {
  const driver = SINGLETONS[vendor];
  const [status, setStatus] = useState(() => driver?.getStatus() || { connected: false });
  const [busy, setBusy] = useState(false);
  const [lastError, setLastError] = useState(null);

  // Subscribe to status changes (only NKEmpowerDriver implements this yet; the
  // XBoat scaffold will gain the same method when it lands).
  useEffect(() => {
    if (!driver || typeof driver.subscribeStatus !== 'function') return undefined;
    return driver.subscribeStatus(setStatus);
  }, [driver]);

  const supported = !!(driver?.constructor?.isSupported?.() ?? false);

  const connect = useCallback(async (opts = {}) => {
    if (!driver) return { ok: false, error: 'No driver for ' + vendor };
    setBusy(true);
    setLastError(null);
    try {
      const res = await driver.requestAndConnect(opts);
      // Run the full NK unlock sequence (CCCDs + unlock byte + keepalive)
      // when the driver supports it; falls back to plain startStream for
      // drivers (XBoat) that don't need the ritual.
      if (typeof driver.startUnlockSequence === 'function') {
        try {
          await driver.startUnlockSequence({ side: opts.side || 'sweep' });
        } catch (unlockErr) {
          // Connection still succeeded — but data won't flow. Surface to caller.
          // eslint-disable-next-line no-console
          console.warn('[NK] startUnlockSequence failed:', unlockErr?.message || unlockErr);
        }
      } else if (typeof driver.startStream === 'function') {
        try {
          await driver.startStream({ side: opts.side || 'sweep' });
        } catch (streamErr) {
          // eslint-disable-next-line no-console
          console.warn('[BLE] startStream failed:', streamErr?.message || streamErr);
        }
      }
      return { ok: true, info: res };
    } catch (e) {
      const msg = e?.message || String(e);
      setLastError(msg);
      return { ok: false, error: msg };
    } finally {
      setBusy(false);
    }
  }, [driver, vendor]);

  const startStream = useCallback(async (opts = {}) => {
    if (!driver || typeof driver.startStream !== 'function') return { ok: false, error: 'Not supported' };
    try {
      await driver.startStream(opts);
      return { ok: true };
    } catch (e) {
      return { ok: false, error: e?.message || String(e) };
    }
  }, [driver]);

  const disconnect = useCallback(async () => {
    if (!driver) return;
    setBusy(true);
    try { await driver.disconnect(); } catch (_e) { /* noop */ }
    setBusy(false);
  }, [driver]);

  const discover = useCallback(async () => {
    if (!driver) return { ok: false, error: 'No driver' };
    setBusy(true);
    setLastError(null);
    try {
      const tree = await driver.discoverGattTree();
      return { ok: true, tree };
    } catch (e) {
      const msg = e?.message || String(e);
      setLastError(msg);
      return { ok: false, error: msg };
    } finally {
      setBusy(false);
    }
  }, [driver]);

  const readDeviceInfo = useCallback(async () => {
    if (!driver || typeof driver.readDeviceInfo !== 'function') return { ok: false, error: 'Not supported' };
    setBusy(true);
    try {
      const info = await driver.readDeviceInfo();
      return { ok: true, info };
    } catch (e) {
      return { ok: false, error: e?.message || String(e) };
    } finally {
      setBusy(false);
    }
  }, [driver]);

  const startLiveCapture = useCallback(async (onFrame) => {
    if (!driver || typeof driver.startLiveCapture !== 'function') return null;
    try {
      return await driver.startLiveCapture(onFrame);
    } catch (e) {
      setLastError(e?.message || String(e));
      return null;
    }
  }, [driver]);

  const readChar = useCallback(async (serviceUuid, charUuid) => {
    if (!driver || typeof driver.readChar !== 'function') return { ok: false, error: 'Not supported' };
    try {
      const r = await driver.readChar(serviceUuid, charUuid);
      return { ok: true, ...r };
    } catch (e) {
      return { ok: false, error: e?.message || String(e) };
    }
  }, [driver]);

  const probeWake = useCallback(async () => {
    if (!driver || typeof driver.probeWake !== 'function') return { ok: false, error: 'Not supported' };
    setBusy(true);
    try {
      const r = await driver.probeWake();
      return { ok: true, ...r };
    } catch (e) {
      return { ok: false, error: e?.message || String(e) };
    } finally {
      setBusy(false);
    }
  }, [driver]);

  const writeConfigBytes = useCallback(async (bytes, opts = {}) => {
    if (!driver || typeof driver.writeConfigBytes !== 'function') return { ok: false, error: 'Not supported' };
    setBusy(true);
    try {
      const r = await driver.writeConfigBytes(bytes, opts);
      return { ok: true, ...r };
    } catch (e) {
      return { ok: false, error: e?.message || String(e) };
    } finally {
      setBusy(false);
    }
  }, [driver]);

  const setRigBySide = useCallback((rigBySide) => {
    driver?.setRigBySide?.(rigBySide);
  }, [driver]);

  return {
    driver,
    status,
    supported,
    busy,
    lastError,
    connect,
    disconnect,
    discover,
    readDeviceInfo,
    startLiveCapture,
    readChar,
    probeWake,
    writeConfigBytes,
    startStream,
    setRigBySide,
  };
}
